// A capture and everything it made, as one thing to drop.
//
// A capture is its carrier task (labelled `via` = the link, `from` = the
// sender) plus any Suggestion with `spec.target_id` = the carrier. What
// accepting a suggestion made is in its `status.created` (tasks, projects,
// pages, lists and the steps of a task), and the items it added to lists are
// the `add_items` ops it kept, whose item ids are fixed. Dropping them is
// ordinary ops in one group, so one undo brings it all back.
import { db as runtimeDb, mutate } from '@/runtime'
import type { Db } from '@/db/schema'
import type { SuggestionResource } from '@/kinds/suggestion'
import { useUi } from '@/state/ui'
import { undoWithFlash } from '@/lib/undo'

export type CaptureParts = {
  carrier: string
  /** resources to delete, the carrier first */
  resources: string[]
  /** pending proposals to turn down */
  pending: string[]
  /** items a kept add_items op put on a list */
  items: { list: string; id: string }[]
}

export const capturePartsOf = async (carrierId: string, db: Db = runtimeDb): Promise<CaptureParts> => {
  const all = await db.resources.where('kind').equals('Suggestion').toArray()
  const cards = (all as SuggestionResource[]).filter(
    (s) => !s.deleted_at && s.spec.type === 'capture' && s.spec.target_id === carrierId,
  )
  const ids = new Set<string>([carrierId])
  const items: CaptureParts['items'] = []
  const pending: string[] = []
  for (const s of cards) {
    if (s.status.state === 'pending') pending.push(s.id)
    for (const c of s.status.created ?? []) if (c.kind !== 'AgentRun') ids.add(c.id)
    if (s.status.state !== 'accepted') continue
    const kept = new Set(s.status.accepted_ops ?? s.spec.ops.map((_, i) => i))
    s.spec.ops.forEach((op, i) => {
      if (!kept.has(i) || op.type !== 'action' || op.action !== 'add_items' || !op.id) return
      const list = (op.args as { items?: { id?: string }[] } | undefined)?.items ?? []
      for (const it of list) if (it.id) items.push({ list: op.id, id: it.id })
    })
  }
  const live: string[] = []
  for (const id of ids) {
    const r = await db.resources.get(id)
    if (r && !r.deleted_at) live.push(id)
  }
  // the carrier first, so a flash can name it
  live.sort((a, b) => (a === carrierId ? -1 : b === carrierId ? 1 : 0))
  return { carrier: carrierId, resources: live, pending, items }
}

/**
 * Drop a whole capture: turn down what still waits, take off the list items
 * it added, delete the carrier and everything it created. One undo restores it.
 */
export const dropCapture = async (carrierId: string, name: string): Promise<void> => {
  const parts = await capturePartsOf(carrierId)
  await mutate.grouped(async () => {
    for (const id of parts.pending)
      await mutate.bookkeeping(() => mutate.action(id, 'dismiss')).catch(() => {})
    for (const it of parts.items) await mutate.action(it.list, 'remove_item', { id: it.id }).catch(() => {})
    for (const id of parts.resources) await mutate.remove(id)
  })
  const n = parts.resources.length + parts.items.length - 1
  useUi
    .getState()
    .showFlash(n > 0 ? `Dropped “${name}” and ${n} more` : `Dropped “${name}”`, 6000, {
      label: 'Undo',
      run: () => void undoWithFlash(),
    })
}
